import React, { useState, useEffect, useRef } from 'react';
import { useNavigate } from 'react-router-dom';
import { Edit2, Check, X, MapPin, Calendar, Users, Shield, Camera, MessageSquare } from 'lucide-react';
import { authAPI, activitiesAPI, connectionsAPI, preferencesAPI } from '../api/client';
import Layout from '../components/Layout';
import '../styles/profile.css';

function getInitials(name) {
  if (!name) return '?';
  const parts = name.trim().split(' ');
  return (parts.length > 1 ? parts[0][0] + parts[parts.length - 1][0] : name[0]).toUpperCase();
}

function formatShortDate(d) {
  if (!d) return 'Anytime';
  return new Date(d).toLocaleDateString('en-US', { month: 'short', day: 'numeric' });
}

export default function Profile() {
  const navigate = useNavigate();
  const [user, setUser]             = useState(null);
  const [myActivities, setMyActs]   = useState([]);
  const [connections, setConns]     = useState([]);
  const [loading, setLoading]       = useState(true);
  const [editingBio, setEditingBio] = useState(false);
  const [bioVal, setBioVal]         = useState('');
  const [editingName, setEditingName] = useState(false);
  const [nameVal, setNameVal]       = useState('');
  const [editingHandles, setEditingHandles] = useState(false);
  const [igVal, setIgVal]           = useState('');
  const [twVal, setTwVal]           = useState('');
  const [saving, setSaving]         = useState(false);
  const [nameError, setNameError]   = useState('');

  // Email notification preferences
  const [emailPrefs, setEmailPrefs] = useState(null);
  const [savingEmailPrefs, setSavingEmailPrefs] = useState(false);

  // 2FA setup state
  const [twoFaCode, setTwoFaCode]   = useState('');
  const [twoFaStep, setTwoFaStep]   = useState('idle'); // idle | phone | sending | code
  const [twoFaError, setTwoFaError] = useState('');
  const [resendingEmail, setResendingEmail] = useState(false);
  const [resendEmailDone, setResendEmailDone] = useState(false);
  const [resendEmailError, setResendEmailError] = useState('');
  const [uploadingAvatar, setUploadingAvatar] = useState(false);
  const avatarInputRef = useRef(null);

  const currentUserId = localStorage.getItem('userId');

  const loadAll = async () => {
    try {
      const [meRes, feedRes, connRes, emailPrefsRes] = await Promise.all([
        authAPI.getMe(),
        activitiesAPI.getFeed(100, 0),
        connectionsAPI.getAll(),
        preferencesAPI.getEmailPrefs().catch(() => null),
      ]);
      const me = meRes.data;
      setUser(me);
      setNameVal(me.display_name || '');
      setBioVal(me.bio || '');
      setIgVal(me.instagram_handle || '');
      setTwVal(me.twitter_handle || '');
      setMyActs((feedRes.data || []).filter(a => a.creator_id === currentUserId));
      setConns(connRes.data || []);
      if (emailPrefsRes?.data) setEmailPrefs(emailPrefsRes.data);
    } catch {}
    setLoading(false);
  };

  useEffect(() => { loadAll(); }, []);

  const saveName = async () => {
    if (!nameVal.trim()) { setNameError('Name cannot be empty'); return; }
    setSaving(true); setNameError('');
    try {
      await authAPI.updateMe({ display_name: nameVal.trim(), bio: bioVal.trim() });
      localStorage.setItem('displayName', nameVal.trim());
      setEditingName(false);
      setEditingBio(false);
      loadAll();
    } catch (e) { setNameError(e.response?.data?.error || 'Failed to save'); }
    setSaving(false);
  };

  const saveBio = async () => {
    setSaving(true);
    try {
      await authAPI.updateMe({ display_name: user.display_name, bio: bioVal.trim() });
      setEditingBio(false);
      loadAll();
    } catch {}
    setSaving(false);
  };

  const saveHandles = async () => {
    setSaving(true);
    try {
      await authAPI.updateMe({ instagram_handle: igVal.trim(), twitter_handle: twVal.trim() });
      setEditingHandles(false);
      loadAll();
    } catch {}
    setSaving(false);
  };

  const send2faCode = async () => {
    setTwoFaStep('sending'); setTwoFaError('');
    try {
      await authAPI.setup2fa();
      setTwoFaStep('code');
    } catch (e) { setTwoFaError(e.response?.data?.error || 'Failed to send code'); setTwoFaStep('phone'); }
  };

  const confirm2faCode = async () => {
    if (!twoFaCode.trim()) return;
    setTwoFaError('');
    try {
      await authAPI.verify2faSetup(twoFaCode.trim());
      setTwoFaStep('idle'); setTwoFaCode('');
      loadAll();
    } catch (e) { setTwoFaError(e.response?.data?.error || 'Incorrect code'); }
  };

  const disable2fa = async () => {
    try { await authAPI.disable2fa(); loadAll(); } catch {}
  };

  const resendVerification = async () => {
    setResendingEmail(true);
    setResendEmailError('');
    try {
      await authAPI.resendVerification();
      setResendEmailDone(true);
    } catch (err) {
      setResendEmailError(err.response?.data?.error || 'Failed to send — please try again.');
    }
    setResendingEmail(false);
  };

  const resizeImage = (file, maxPx = 600) => new Promise((resolve) => {
    const img = new Image();
    const url = URL.createObjectURL(file);
    img.onload = () => {
      const scale = Math.min(1, maxPx / Math.max(img.width, img.height));
      const canvas = document.createElement('canvas');
      canvas.width = Math.round(img.width * scale);
      canvas.height = Math.round(img.height * scale);
      canvas.getContext('2d').drawImage(img, 0, 0, canvas.width, canvas.height);
      URL.revokeObjectURL(url);
      canvas.toBlob(resolve, 'image/jpeg', 0.82);
    };
    img.src = url;
  });

  const handleAvatarChange = async (e) => {
    const file = e.target.files?.[0];
    if (!file) return;
    setUploadingAvatar(true);
    try {
      const resized = await resizeImage(file);
      const fd = new FormData();
      fd.append('avatar', resized, 'avatar.jpg');
      await authAPI.uploadAvatar(fd);
      loadAll();
    } catch (err) {
      alert(err.response?.data?.error || 'Upload failed — please try a different photo.');
    }
    setUploadingAvatar(false);
    e.target.value = '';
  };

  const acceptConn = async (id) => { await connectionsAPI.accept(id).catch(() => {}); loadAll(); };
  const removeConn = async (id) => { await connectionsAPI.delete(id).catch(() => {}); loadAll(); };

  const toggleEmailPref = async (key) => {
    if (!emailPrefs || savingEmailPrefs) return;
    const updated = { ...emailPrefs, [key]: emailPrefs[key] ? 0 : 1 };
    setEmailPrefs(updated);
    setSavingEmailPrefs(true);
    await preferencesAPI.updateEmailPrefs({ [key]: updated[key] }).catch(() => {});
    setSavingEmailPrefs(false);
  };

  if (loading) return <Layout title="Profile"><div className="prof-loading">Loading…</div></Layout>;

  const pending  = connections.filter(c => c.status === 'pending' && c.connected_user_id === currentUserId);
  const accepted = connections.filter(c => c.status === 'accepted');
  const upcoming = myActivities.filter(a => !a.is_cancelled && a.start_time && new Date(a.start_time) >= new Date());
  const past     = myActivities.filter(a => !a.is_cancelled && (!a.start_time || new Date(a.start_time) < new Date()));

  return (
    <Layout title="Profile">
      <div className="prof-page">

        {/* Header card */}
        <div className="prof-header-card">
          <div className="prof-hero-banner" />
          <div className="prof-hero-row">
            <div className="prof-avatar-wrap">
              <div
                className="prof-avatar-clickable"
                onClick={() => avatarInputRef.current?.click()}
                title="Change photo"
              >
                {user?.avatar_url
                  ? <img src={user.avatar_url} alt="avatar" className="prof-avatar-img" />
                  : <div className="prof-avatar">{getInitials(user?.display_name || user?.phone_email)}</div>}
                <div className="prof-avatar-overlay">
                  {uploadingAvatar ? '…' : <Camera size={20} />}
                </div>
                <div className="prof-avatar-camera-badge">
                  {uploadingAvatar ? <span style={{ fontSize: 10 }}>…</span> : <Camera size={12} />}
                </div>
              </div>
              <input
                ref={avatarInputRef}
                type="file"
                accept="image/*"
                style={{ display: 'none' }}
                onChange={handleAvatarChange}
              />
            </div>

          <div className="prof-identity">
            {editingName ? (
              <div className="prof-name-edit">
                <input
                  className="prof-name-input"
                  value={nameVal}
                  onChange={e => setNameVal(e.target.value)}
                  autoFocus
                  onKeyDown={e => e.key === 'Enter' && saveName()}
                />
                {nameError && <p className="prof-error">{nameError}</p>}
                <div className="prof-edit-actions">
                  <button className="prof-icon-btn blue" onClick={saveName} disabled={saving}><Check size={15} /></button>
                  <button className="prof-icon-btn" onClick={() => { setEditingName(false); setNameVal(user?.display_name || ''); }}><X size={15} /></button>
                </div>
              </div>
            ) : (
              <div className="prof-name-row">
                <h1 className="prof-name">{user?.display_name || 'No name set'}</h1>
                <button className="prof-icon-btn" onClick={() => setEditingName(true)} title="Edit name"><Edit2 size={14} /></button>
              </div>
            )}
            <p className="prof-email">{user?.phone_email}</p>

            {editingBio ? (
              <div className="prof-bio-edit">
                <textarea
                  className="prof-bio-input"
                  value={bioVal}
                  onChange={e => setBioVal(e.target.value)}
                  placeholder="Short bio…"
                  rows={2}
                  autoFocus
                />
                <div className="prof-edit-actions">
                  <button className="prof-icon-btn blue" onClick={saveBio} disabled={saving}><Check size={15} /></button>
                  <button className="prof-icon-btn" onClick={() => { setEditingBio(false); setBioVal(user?.bio || ''); }}><X size={15} /></button>
                </div>
              </div>
            ) : (
              <div className="prof-bio-row">
                <p className="prof-bio" onClick={() => setEditingBio(true)}>
                  {user?.bio || <span className="prof-bio-placeholder">Add a bio…</span>}
                </p>
                {user?.bio && <button className="prof-icon-btn" onClick={() => setEditingBio(true)}><Edit2 size={13} /></button>}
              </div>
            )}

            {/* Social handles + phone */}
            {editingHandles ? (
              <div className="prof-handles-edit">
                <div className="prof-handle-input-row">
                  <span className="prof-handle-prefix">ig/</span>
                  <input className="prof-handle-input" value={igVal} onChange={e => setIgVal(e.target.value)} placeholder="yourhandle" />
                </div>
                <div className="prof-handle-input-row">
                  <span className="prof-handle-prefix">x/</span>
                  <input className="prof-handle-input" value={twVal} onChange={e => setTwVal(e.target.value)} placeholder="yourhandle" />
                </div>
                <div className="prof-edit-actions">
                  <button className="prof-icon-btn blue" onClick={saveHandles} disabled={saving}><Check size={15} /></button>
                  <button className="prof-icon-btn" onClick={() => { setEditingHandles(false); setIgVal(user?.instagram_handle || ''); setTwVal(user?.twitter_handle || ''); }}><X size={15} /></button>
                </div>
              </div>
            ) : (
              <div className="prof-handles-row" onClick={() => setEditingHandles(true)}>
                {user?.instagram_handle
                  ? <span className="prof-handle">ig/<strong>@{user.instagram_handle}</strong></span>
                  : <span className="prof-handle-placeholder">Add Instagram…</span>}
                {user?.twitter_handle
                  ? <span className="prof-handle">x/<strong>@{user.twitter_handle}</strong></span>
                  : <span className="prof-handle-placeholder">Add X…</span>}
                <button className="prof-icon-btn" style={{ marginLeft: 'auto' }}><Edit2 size={13} /></button>
              </div>
            )}
          </div>
          </div>{/* end prof-hero-row */}

          {/* Stats row */}
          <div className="prof-stats">
            <div className="prof-stat">
              <span className="prof-stat-num">{myActivities.length}</span>
              <span className="prof-stat-label">Plans</span>
            </div>
            <div className="prof-stat-divider" />
            <div className="prof-stat">
              <span className="prof-stat-num">{accepted.length}</span>
              <span className="prof-stat-label">Friends</span>
            </div>
            <div className="prof-stat-divider" />
            <div className="prof-stat">
              <span className="prof-stat-num">{new Date(user?.created_at).toLocaleDateString('en-US', { month: 'short', year: 'numeric' })}</span>
              <span className="prof-stat-label">Joined</span>
            </div>
          </div>
        </div>

        <div className="prof-grid">
          <div className="prof-main">

            {/* Connection requests — always visible */}
            <div className={`prof-card${pending.length > 0 ? ' prof-card-highlight' : ''}`}>
              <h2 className="prof-card-title">
                Friend Requests
                {pending.length > 0
                  ? <span className="prof-badge">{pending.length}</span>
                  : <span className="prof-badge prof-badge-empty">0</span>}
              </h2>
              {pending.length === 0 ? (
                <p className="prof-empty">No pending requests.</p>
              ) : pending.map(c => (
                <div key={c.id} className="prof-conn-row">
                  <div className="prof-conn-avatar">{getInitials(c.display_name)}</div>
                  <span className="prof-conn-name">{c.display_name || c.phone_email}</span>
                  <div className="prof-conn-actions">
                    <button className="prof-btn-accept" onClick={() => acceptConn(c.id)}>Accept</button>
                    <button className="prof-btn-decline" onClick={() => removeConn(c.id)}>Decline</button>
                  </div>
                </div>
              ))}
            </div>

            {/* Upcoming activities */}
            <div className="prof-card">
              <div className="prof-card-header">
                <h2 className="prof-card-title">
                  <Calendar size={16} /> Upcoming Plans
                </h2>
                <button className="prof-card-action" onClick={() => navigate('/create-activity')}>+ New</button>
              </div>
              {upcoming.length === 0 ? (
                <p className="prof-empty">No upcoming plans. <button className="prof-link" onClick={() => navigate('/create-activity')}>Create one →</button></p>
              ) : (
                upcoming.slice(0, 5).map(a => (
                  <div key={a.id} className="prof-act-row" onClick={() => navigate(`/activity/${a.id}`)}>
                    <div className="prof-act-dot" />
                    <div className="prof-act-info">
                      <span className="prof-act-title">{a.title || a.activity_type}</span>
                      <span className="prof-act-meta">{formatShortDate(a.start_time)}{a.location_name ? ` · ${a.location_name}` : ''}</span>
                    </div>
                    <span className="prof-act-rsvp">{(a.rsvps || []).filter(r => r.response_type === 'im_in').length} going</span>
                  </div>
                ))
              )}
            </div>

            {/* Past activities */}
            {past.length > 0 && (
              <div className="prof-card">
                <h2 className="prof-card-title"><MapPin size={16} /> Past Plans</h2>
                {past.slice(0, 4).map(a => (
                  <div key={a.id} className="prof-act-row past" onClick={() => navigate(`/activity/${a.id}`)}>
                    <div className="prof-act-dot past" />
                    <div className="prof-act-info">
                      <span className="prof-act-title">{a.title || a.activity_type}</span>
                      <span className="prof-act-meta">{formatShortDate(a.start_time)}</span>
                    </div>
                  </div>
                ))}
              </div>
            )}
          </div>

          <div className="prof-side">
            {/* Friends */}
            <div className="prof-card">
              <div className="prof-card-header">
                <h2 className="prof-card-title"><Users size={16} /> Friends</h2>
                <button className="prof-card-action" onClick={() => navigate('/find-friends')}>+ Add</button>
              </div>
              {accepted.length === 0 ? (
                <p className="prof-empty">No friends yet. <button className="prof-link-btn" onClick={() => navigate('/find-friends')}>Find people you know →</button></p>
              ) : (
                accepted.slice(0, 4).map(c => {
                  const friendId = c.user_id === currentUserId ? c.connected_user_id : c.user_id;
                  return (
                  <div key={c.id} className="prof-conn-row" style={{ cursor: 'pointer' }} onClick={() => navigate(`/profile/${friendId}`)}>
                    <div className="prof-conn-avatar">{getInitials(c.display_name)}</div>
                    <span className="prof-conn-name">{c.display_name || c.phone_email}</span>
                    <button className="prof-btn-msg" onClick={e => { e.stopPropagation(); navigate(`/messages/${friendId}`); }}>
                      <MessageSquare size={13} /> Message
                    </button>
                  </div>
                  );
                })
              )}
              {accepted.length > 0 && (
                <button className="prof-show-more-btn" onClick={() => navigate('/friends')}>
                  {accepted.length > 4 ? `See all ${accepted.length} friends →` : 'See all friends →'}
                </button>
              )}
            </div>


            {/* Email Notifications */}
            {emailPrefs !== null && (
              <div className="prof-card">
                <h2 className="prof-card-title">Email Notifications</h2>
                {[
                  { key: 'email_friend_requests', label: 'Friend requests' },
                  { key: 'email_group_invites',   label: 'Group invites' },
                  { key: 'email_activity_posts',  label: 'New activities' },
                  { key: 'email_messages',        label: 'Messages' },
                ].map(({ key, label }) => (
                  <div key={key} className="prof-security-row">
                    <span className="prof-security-label">{label}</span>
                    <button
                      className={`prof-email-toggle${emailPrefs[key] ? ' on' : ''}`}
                      onClick={() => toggleEmailPref(key)}
                      disabled={savingEmailPrefs}
                    >
                      <span className="prof-email-toggle-thumb" />
                    </button>
                  </div>
                ))}
              </div>
            )}

            {/* Security */}
            <div className="prof-card">
              <h2 className="prof-card-title"><Shield size={16} /> Security</h2>

              {/* Email verification */}
              {user?.phone_email?.includes('@') && (
                <div className="prof-security-row">
                  <div>
                    <span className="prof-security-label">Email</span>
                    <span className={`prof-security-badge ${user.email_verified ? 'verified' : 'unverified'}`}>
                      {user.email_verified ? 'Verified' : 'Not verified'}
                    </span>
                  </div>
                  {!user.email_verified && !resendEmailDone && (
                    <button className="prof-security-btn" onClick={resendVerification} disabled={resendingEmail}>
                      {resendingEmail ? 'Sending…' : 'Resend email'}
                    </button>
                  )}
                  {resendEmailDone && <span className="prof-security-sent">Sent! Check your inbox.</span>}
                  {resendEmailError && <span className="prof-security-error">{resendEmailError}</span>}
                </div>
              )}

              {/* 2FA */}
              <div className="prof-security-row" style={{ flexDirection: 'column', alignItems: 'flex-start', gap: 8 }}>
                <div style={{ display: 'flex', alignItems: 'center', gap: 8, width: '100%' }}>
                  <div>
                    <span className="prof-security-label">Two-factor auth</span>
                    <span className={`prof-security-badge ${user?.two_fa_enabled ? 'verified' : 'unverified'}`}>
                      {user?.two_fa_enabled ? 'Enabled' : 'Disabled'}
                    </span>
                  </div>
                  {user?.two_fa_enabled ? (
                    <button className="prof-security-btn danger" onClick={disable2fa} style={{ marginLeft: 'auto' }}>
                      Disable
                    </button>
                  ) : twoFaStep === 'idle' ? (
                    <button className="prof-security-btn" onClick={() => setTwoFaStep('phone')} style={{ marginLeft: 'auto' }}>
                      Enable
                    </button>
                  ) : null}
                </div>

                {!user?.two_fa_enabled && (twoFaStep === 'phone' || twoFaStep === 'sending') && (
                  <div className="prof-2fa-setup">
                    <p className="prof-security-hint">A 6-digit code will be sent to your email ({user?.phone_email})</p>
                    {twoFaError && <p className="prof-error">{twoFaError}</p>}
                    <div className="prof-edit-actions">
                      <button className="prof-icon-btn blue" onClick={send2faCode} disabled={twoFaStep === 'sending'}>
                        {twoFaStep === 'sending' ? '…' : <Check size={15} />}
                      </button>
                      <button className="prof-icon-btn" onClick={() => { setTwoFaStep('idle'); setTwoFaError(''); }}>
                        <X size={15} />
                      </button>
                    </div>
                  </div>
                )}

                {!user?.two_fa_enabled && twoFaStep === 'code' && (
                  <div className="prof-2fa-setup">
                    <p className="prof-security-hint">Check your email for a 6-digit code</p>
                    <input
                      className="prof-security-input"
                      type="text"
                      inputMode="numeric"
                      maxLength={6}
                      value={twoFaCode}
                      onChange={e => setTwoFaCode(e.target.value.replace(/\D/g, ''))}
                      placeholder="000000"
                      autoFocus
                      style={{ letterSpacing: '0.3em', textAlign: 'center' }}
                    />
                    {twoFaError && <p className="prof-error">{twoFaError}</p>}
                    <div className="prof-edit-actions">
                      <button className="prof-icon-btn blue" onClick={confirm2faCode} disabled={twoFaCode.length < 6}>
                        <Check size={15} />
                      </button>
                      <button className="prof-icon-btn" onClick={() => { setTwoFaStep('idle'); setTwoFaCode(''); setTwoFaError(''); }}>
                        <X size={15} />
                      </button>
                    </div>
                  </div>
                )}
              </div>

              <button className="prof-quick-link" style={{ marginTop: 4 }} onClick={() => navigate('/forgot-password')}>
                Change password →
              </button>
            </div>
          </div>
        </div>
      </div>
    </Layout>
  );
}
